<script setup lang="ts">
import { onMounted, onUpdated, ref } from 'vue'
// eslint-disable-next-line import/no-named-as-default
import Sortable from 'sortablejs'
import type { SortItem } from '@core/types'
import type { TableOptions } from '@core/stores/user'
import CompetenceCopyDialog from '@/components/dialogs/CompetenceCopyDialog.vue'
import { App } from '@/types/generated'
import UserData = App.Http.Controllers.Auth.UserData

const tableBodyRef = ref<HTMLElement | null>(null)
const sortableInstance = ref<Sortable | null>(null)
const { t } = useI18n()
const table = 'competencies'

const userData = useCookie<UserData>('userData')
const userRoles: string[] = userData.value?.roles ?? []

definePage({
  meta: {
    action: 'read',
    subject: 'competencies',
  },
})

const headers = [
  { title: t(`${table}-page.title`), key: 'title', sortable: false },
  { title: t(`${table}-page.skills`), key: 'skills', sortable: false },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'display_order',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)

const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>('display_order')
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>('asc')
const itemForDelete = ref()
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isCopyDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

interface AssistantItem {
  id: string
  description: string
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const { data: aiAssistantsList } = await useApi<any>('/ai-assistants/all')
const { data: departmentsList } = await useApi<any>('/departments/all')

let aiAssistants: AssistantItem[] = [{ description: '', id: '1' }]
if (aiAssistantsList?.value)
  aiAssistants = aiAssistantsList.value

interface DepartmentItem {
  id: string
  title: string
}

let departments: DepartmentItem[] = []
if (departmentsList?.value)
  departments = departmentsList.value

const { data: itemsData, execute: fetchItems, isFinished: tableFetchIsFinished } = await useApi<any>(createUrl(`/${table}/`,
  {
    query: {
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
    },
  },
))

const isPageSwitching = ref<boolean>(false)

const updateList = async () => {
  isLoading.value = true
  await nextTick()
  await fetchItems()
  isLoading.value = false
}

const deleteItem = async () => {
  await $api(`/${table}/`, {
    method: 'DELETE',
    body: {
      id: itemForDelete.value.id,
    },
  })
  await updateList()
}

const deleteConfirmation = async (item: any) => {
  isDeleteConfirmDialogVisible.value = !isDeleteConfirmDialogVisible.value
  itemForDelete.value = item
}

const showEditDialog = async (item: any) => {
  isAddDialogOpen.value = !isAddDialogOpen.value
  currenItem.value = item
}

const showCopyDialog = async (item: any) => {
  isCopyDialogOpen.value = !isCopyDialogOpen.value
  currenItem.value = item
}

const items = computed(() => itemsData.value?.data)

const total = computed(() => itemsData.value?.total)
const lastPage = computed(() => itemsData.value?.last_page)
const pageSwitched = ref<string | undefined>(undefined)
const tableKey = ref(0)

const handleReorder = async (oldIndex: number, newIndex: number) => {
  if (!items.value)
    return
  if (oldIndex === newIndex && pageSwitched.value === undefined)
    return

  // Готовим данные для сервера
  if (pageSwitched.value !== undefined) {
    isPageSwitching.value = true

    // Сохраняем информацию о том, что и куда нужно переместить
    const currentItemId = itemsData.value.data[oldIndex].id
    const targetPage = pageSwitched.value === 'next' ? page.value + 1 : page.value - 1

    const newDisplayOrder = pageSwitched.value === 'next'
      ? (itemsData.value.data[currentTableOptions?.perPage ? (currentTableOptions?.perPage - 1) : 0].display_order ?? 0) + 1
      : (itemsData.value.data[0].display_order ?? 0) - 1

    // Отправляем запрос на изменение порядка
    await $api(`/${table}/reorder`, {
      method: 'POST',
      body: {
        currentItemId,
        newDisplayOrder,
      },
    })

    // Изменяем страницу
    page.value = targetPage

    // Ждем обновления DOM
    await nextTick()

    // Увеличиваем ключ таблицы для принудительного обновления
    tableKey.value++

    // Ждем окончания обновления DOM перед обновлением Sortable
    await nextTick()

    // Сбрасываем флаги и принудительно реинициализируем Sortable
    pageSwitched.value = undefined
    isPageSwitching.value = false

    return
  }

  // Отправляем обновлённый порядок на сервер
  await $api(`/${table}/reorder`, {
    method: 'POST',
    body: {
      currentItemId: itemsData.value.data[oldIndex].id,
      newDisplayOrder: itemsData.value.data[newIndex].display_order,
    },
  })
  await updateList()
}

const highlightedRow = ref<HTMLElement | null>(null)

const highlightRow = (type: 'top' | 'bottom') => {
  if (!tableBodyRef.value)
    return

  const rows = tableBodyRef.value.querySelectorAll('tr')

  if (rows.length === 0)
    return

  if (highlightedRow.value)
    highlightedRow.value.classList.remove('highlight-row')

  if (type === 'top')
    highlightedRow.value = rows[0] as HTMLElement
  else if (type === 'bottom')
    highlightedRow.value = rows[rows.length - 1] as HTMLElement

  tableBodyRef.value.classList.add(`highlight-${type}`)
}

const clearHighlight = () => {
  if (highlightedRow.value) {
    tableBodyRef.value?.classList.remove('highlight-top')
    tableBodyRef.value?.classList.remove('highlight-bottom')
    highlightedRow.value = null
  }
}

const handleAutoPageSwitch = (event: DragEvent) => {
  if (!event)
    return

  const tableHead = document.querySelector('.v-data-table__thead')
  const pagination = document.querySelector('.v-pagination')

  if (!tableHead || !pagination)
    return

  const headRect = tableHead.getBoundingClientRect()
  const paginationRect = pagination.getBoundingClientRect()
  const mouseY = event.clientY

  if (mouseY >= headRect.top && mouseY <= headRect.bottom && page.value > 1)
    pageSwitched.value = 'prev'

  else if (mouseY >= paginationRect.top && mouseY <= paginationRect.bottom && page.value < lastPage.value)
    pageSwitched.value = 'next'
}

const onDragOverHighlight = (event: DragEvent) => {
  const tableHead = document.querySelector('.v-data-table__thead')
  const pagination = document.querySelector('.v-pagination')

  if (!tableHead || !pagination)
    return

  const headRect = tableHead.getBoundingClientRect()
  const paginationRect = pagination.getBoundingClientRect()
  const mouseY = event.clientY

  if (mouseY >= headRect.top && mouseY <= headRect.bottom)
    highlightRow('top')

  else if (mouseY >= paginationRect.top && mouseY <= paginationRect.bottom)
    highlightRow('bottom')

  else
    clearHighlight()
}

const initSortable = () => {
  if (sortableInstance.value)
    sortableInstance.value.destroy()

  if (tableBodyRef.value) {
    sortableInstance.value = new Sortable(tableBodyRef.value, {
      animation: 200, // Длительность анимации в миллисекундах (рекомендую 150-250)
      draggable: 'tr',
      onStart: () => {
        if (tableBodyRef.value)
          tableBodyRef.value.classList.add('is-sorting')

        window.addEventListener('dragover', onDragOverHighlight)
      },
      onEnd: evt => {
        if (tableBodyRef.value)
          tableBodyRef.value.classList.remove('is-sorting')

        window.removeEventListener('dragover', onDragOverHighlight)
        clearHighlight()
        handleAutoPageSwitch(evt.originalEvent as DragEvent) // добавляем сюда проверку
        handleReorder(evt.oldIndex, evt.newIndex)
      },

    })
  }
}

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})

watch(page, async (newPage, oldPage) => {
  if (newPage !== oldPage) {
    // Ждем обновления данных
    await nextTick()

    // Принудительно реинициализируем Sortable после завершения обновления DOM
    setTimeout(() => {
      initSortable()
    }, 100)
  }
})

watch(tableFetchIsFinished, val => {
  isLoading.value = !val
})
watch(tableKey, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    // Ждем обновления данных
    await nextTick()

    tableBodyRef.value = document.querySelector('.v-data-table__tbody')
  }
})

onMounted(() => {
  nextTick(() => {
    tableBodyRef.value = document.querySelector('.v-data-table__tbody')
    initSortable()
  })
})

onUpdated(() => {
  nextTick(() => {
    tableBodyRef.value = document.querySelector('.v-data-table__tbody')
    setTimeout(() => {
      initSortable()
    }, 50)
  })
})
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            prepend-icon="tabler-plus"
            @click="isAddDialogOpen = !isAddDialogOpen"
          >
            {{ t(`${table}-page.add`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          :key="tableKey"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :items="items"
          :items-length="total ?? 0"
          class="text-no-wrap sort-draggable"
          @update:options="updateOptions"
        >
          <template #item.actions="{ item }">
            <div class="d-flex align-items-end justify-end">
              <IconBtn
                v-if="userIsAdmin(userRoles) || userIsScopedAdmin(userRoles)"
                tag="a"
                :href="`/api/competencies/export-docx/${item.id}`"
                target="_blank"
                rel="noopener noreferrer"
                :title="t(`${table}-page.export-docx`)"
              >
                <VIcon icon="tabler-file-type-doc" />
              </IconBtn>
              <IconBtn>
                <VIcon
                  icon="tabler-edit"
                  :title="t(`${table}-page.edit`)"
                  @click="showEditDialog(item)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  icon="tabler-copy"
                  :title="t(`copy`)"
                  @click="showCopyDialog(item)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  :title="t(`${table}-page.delete`)"
                  icon="tabler-trash"
                  @click="deleteConfirmation(item)"
                />
              </IconBtn>
            </div>
          </template>
          <template #item.title="{ item }">
            {{ item.title }}
            <VBadge
              v-if="item.ai_assistant_id !== null"
              :offset-x="-18"
              :offset-y="-4"
              class="v-badge--tonal"
              color="success"
              content="1"
            >
              <template #badge>
                AI
              </template>
            </VBadge>
          </template>
          <template #item.skills="{ item }">
            <VBtn
              variant="text"
              :to="{ name: 'games-competencies-skills', query: { competence: item.id } }"
            >
              {{ item.skills_count }}
              <VIcon
                end
                icon="tabler-arrow-right"
              />
            </VBtn>
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.max(1, Math.ceil((total || 0) / itemsPerPage))"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.max(1, Math.ceil((total || 0) / itemsPerPage)), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
    <CompetenceAddEditDialog
      v-model:isDialogVisible="isAddDialogOpen"
      :competence="currenItem"
      :ai-assistants="aiAssistants"
      :departments="departments"
      @submit="updateList"
    />
    <CompetenceCopyDialog
      v-model:isDialogVisible="isCopyDialogOpen"
      :competence-id="currenItem?.id"
      @submit="updateList"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('confirmation.sure')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
  </div>
</template>

<style lang="scss">
.ProseMirror-focused{
  border: none;
}
</style>
